/**
 * CompanionProvider — the layer above the app (context bus + state machine).
 *
 * Mounted once per protected shell (see routes.ts), ABOVE the page, so it
 * survives in-shell navigation: the queue, the open/closed panel state and any
 * in-flight poll persist as the user moves between routes. It is the single
 * source of truth the `CompanionLayer` overlay renders.
 *
 * The context bus: the provider reads the current route from `useLocation`, and
 * pages can register the object they're looking at via `useCompanionObject(id)`.
 * Both feed the server's context boost so what's relevant to where the user
 * stands rises to the top — without losing the global queue in the background.
 *
 * Spec: docs/specs/COMPANION.md §3, §8, §12.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";

import {
  actOnCompanionItem,
  fetchCompanion,
  type CompanionAction,
  type CompanionResponse,
} from "../../../lib/companion";
import { useAuth } from "../../AuthContext";

const POLL_OPEN_MS = 30_000;
const POLL_CLOSED_MS = 120_000;

interface CompanionContextValue {
  /** False when the flag is off OR no workspace — callers fall back to Watch+Bell. */
  enabled: boolean;
  data: CompanionResponse | null;
  loading: boolean;
  /** Is the expanded panel open? */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Act on an item; optimistically drops it, then re-polls. */
  act: (key: string, action: CompanionAction, snoozeMinutes?: number) => Promise<void>;
  /** Context bus — register / clear the object the current page is showing. */
  setActiveObject: (id: string | null) => void;
  refresh: () => Promise<void>;
}

const CompanionContext = createContext<CompanionContextValue | null>(null);

export function CompanionProvider({ children }: { children: React.ReactNode }) {
  const { me } = useAuth();
  const workspaceId = me?.workspace?.id ?? null;
  const location = useLocation();
  const route = location.pathname;

  const [data, setData] = useState<CompanionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeObject, setActiveObject] = useState<string | null>(null);

  // Keep the latest route/object in refs so the polling loop reads fresh values
  // without re-subscribing on every navigation.
  const routeRef = useRef(route);
  const objectRef = useRef(activeObject);
  routeRef.current = route;
  objectRef.current = activeObject;

  const enabled = (data?.enabled ?? false) && workspaceId !== null;

  const poll = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const resp = await fetchCompanion(workspaceId, {
        route: routeRef.current,
        object: objectRef.current,
      });
      setData(resp);
    } catch {
      // Best-effort — the Companion never blocks the user. Keep last data.
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  // Initial fetch + cadence (faster while the panel is open).
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    const run = () => {
      if (!cancelled) void poll();
    };
    run();
    const id = window.setInterval(run, open ? POLL_OPEN_MS : POLL_CLOSED_MS);
    const onRefresh = () => run();
    window.addEventListener("meridian:refresh", onRefresh);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("meridian:refresh", onRefresh);
    };
  }, [workspaceId, open, poll]);

  // Re-poll promptly when the route or the open object changes, so the context
  // boost re-ranks without waiting for the next interval.
  useEffect(() => {
    if (!workspaceId) return;
    const t = window.setTimeout(() => void poll(), 250);
    return () => window.clearTimeout(t);
  }, [route, activeObject, workspaceId, poll]);

  const act = useCallback(
    async (key: string, action: CompanionAction, snoozeMinutes?: number) => {
      if (!workspaceId) return;
      // Optimistic: drop the item locally so the panel feels instant.
      setData((prev) =>
        prev
          ? {
              ...prev,
              needs_you: prev.needs_you.filter((i) => i.key !== key),
              feed: prev.feed.filter((i) => i.key !== key),
              interrupt:
                prev.interrupt && prev.interrupt.key === key ? null : prev.interrupt,
            }
          : prev,
      );
      try {
        await actOnCompanionItem(workspaceId, {
          key,
          action,
          snooze_minutes: snoozeMinutes,
        });
      } catch {
        // ignore — next poll reconciles truth
      }
      void poll();
    },
    [workspaceId, poll],
  );

  const value = useMemo<CompanionContextValue>(
    () => ({
      enabled,
      data,
      loading,
      open,
      setOpen,
      act,
      setActiveObject,
      refresh: poll,
    }),
    [enabled, data, loading, open, act, poll],
  );

  return (
    <CompanionContext.Provider value={value}>
      {children}
    </CompanionContext.Provider>
  );
}

/** Read the Companion state. Returns null outside a provider (e.g. login). */
export function useCompanion(): CompanionContextValue | null {
  return useContext(CompanionContext);
}

/**
 * Context-bus hook for pages: register the object the page is showing so the
 * Companion can boost what's relevant to it. Clears on unmount.
 *
 *     useCompanionObject(modelId);   // on a model page
 */
export function useCompanionObject(objectId: string | null | undefined): void {
  const ctx = useContext(CompanionContext);
  const setActiveObject = ctx?.setActiveObject;
  useEffect(() => {
    if (!setActiveObject) return;
    setActiveObject(objectId ?? null);
    return () => setActiveObject(null);
  }, [objectId, setActiveObject]);
}
